import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Badge, Button, ButtonGroup, Card, Col, Row, Spinner, ToggleButton } from 'react-bootstrap'
import { apiClient } from '../services/api/client'
import { getAutoRefreshIntervalMs, useAutoRefresh } from '../hooks/useAutoRefresh'

const normalizeSubcode = (value) => {
  return (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
}

const formatCurrentDate = () => {
  const date = new Date()
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`
}

const formatAutoRefresh = () => {
  const intervalMs = getAutoRefreshIntervalMs()
  if (intervalMs <= 0) return 'Automatické obnovení vypnuto.'
  const seconds = Math.round(intervalMs / 1000)
  if (seconds % 60 !== 0) return `Automatické obnovení každých ${seconds} s.`
  const minutes = seconds / 60
  if (minutes === 1) return 'Automatické obnovení každou minutu.'
  if (minutes >= 2 && minutes <= 4) return `Automatické obnovení každé ${minutes} minuty.`
  return `Automatické obnovení každých ${minutes} minut.`
}

const MECHANIC_ORDER_FILTERS = [
  { value: 'prijato', label: 'Přijato', icon: 'bi-clipboard-check', color: 'var(--bs-secondary)' },
  { value: 'dilna', label: 'Dílna', icon: 'bi-wrench', color: 'var(--bs-primary)' },
  { value: 'provedeno', label: 'Provedeno', icon: 'bi-hourglass-split', color: 'var(--bs-danger)' },
  { value: 'castecne-vyrizeno', label: 'Částečně vyřízeno', icon: 'bi-check-circle', color: 'var(--bs-warning-text-emphasis)' },
  { value: 'vyrizeno', label: 'Vyřízeno', icon: 'bi-check-circle-fill', color: 'var(--bs-success)' },
  { value: 'all', label: 'Vše', icon: 'bi-grid', color: 'var(--bs-primary)' }
]
const MECHANIC_STATUS_FILTER_KEY = 'mechanicOrderStatusFilter'

const DashboardMechanic = () => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState([])
  const [strediskoNazev, setStrediskoNazev] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [statusFilter, setStatusFilter] = useState(() => {
    const savedFilter = sessionStorage.getItem(MECHANIC_STATUS_FILTER_KEY)
    return MECHANIC_ORDER_FILTERS.some((filter) => filter.value === savedFilter) ? savedFilter : 'dilna'
  })

  const changeStatusFilter = (value) => {
    setStatusFilter(value)
    sessionStorage.setItem(MECHANIC_STATUS_FILTER_KEY, value)
  }

  const filteredOrders = useMemo(() => (orders || []).filter((order) => {
    if (statusFilter === 'all') return true
    return normalizeSubcode(order?.stav) === statusFilter
  }), [orders, statusFilter])

  const statusMeta = (value) => {
    const normalized = normalizeSubcode(value)
    if (normalized === 'prijato') return { label: 'Přijato', color: 'var(--bs-secondary-color)', background: 'rgba(var(--bs-secondary-rgb), 0.08)', border: 'rgba(var(--bs-secondary-rgb), 0.45)' }
    if (normalized === 'dilna') return { label: 'Dílna', color: 'var(--bs-primary)', background: 'rgba(var(--bs-primary-rgb), 0.10)', border: 'rgba(var(--bs-primary-rgb), 0.55)' }
    if (normalized === 'provedeno') return { label: 'Provedeno', color: 'var(--bs-danger)', background: 'rgba(var(--bs-danger-rgb), 0.10)', border: 'rgba(var(--bs-danger-rgb), 0.50)' }
    if (normalized === 'castecne-vyrizeno') return { label: 'Částečně vyřízeno', color: 'var(--bs-warning-text-emphasis)', background: 'rgba(var(--bs-warning-rgb), 0.16)', border: 'rgba(var(--bs-warning-rgb), 0.65)' }
    if (normalized === 'vyrizeno') return { label: 'Vyřízeno', color: 'var(--bs-success)', background: 'rgba(var(--bs-success-rgb), 0.11)', border: 'rgba(var(--bs-success-rgb), 0.50)' }
    return { label: value || 'Bez stavu', color: 'var(--bs-secondary-color)', background: 'rgba(var(--bs-secondary-rgb), 0.08)', border: 'rgba(var(--bs-secondary-rgb), 0.45)' }
  }

  const loadOrders = async () => {
    try {
      setLoading(true)
      setError('')
      const data = await apiClient.get('/objednavky/mechanik/dilna')
      if (Array.isArray(data)) {
        setOrders(data)
        setStrediskoNazev('')
      } else {
        setOrders(Array.isArray(data?.orders) ? data.orders : [])
        setStrediskoNazev(String(data?.strediskoNazev || '').trim())
      }
    } catch (e) {
      setOrders([])
      setStrediskoNazev('')
      setError(e.message || 'Nepodařilo se načíst objednávky.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [])

  useAutoRefresh(loadOrders)

  return (
    <div className='p-2'>
      <div className="mb-3">
        <div>
          <h4 className="mb-0">Zakázky</h4>
          <div className="small text-muted">Přehled zakázek pro aktuální den ({formatCurrentDate()}). {formatAutoRefresh()}</div>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
      <ButtonGroup className="mechanic-status-filters" aria-label="Filtr stavu objednávek">
        {MECHANIC_ORDER_FILTERS.map((filter) => (
          <ToggleButton
            key={filter.value}
            id={`mechanic-filter-${filter.value}`}
            type="radio"
            name="mechanic-order-filter"
            value={filter.value}
            variant="outline-primary"
            className="btn-lg mechanic-status-filter-button"
            checked={statusFilter === filter.value}
            onChange={(event) => changeStatusFilter(event.currentTarget.value)}
            title={filter.label}
            aria-label={filter.label}
          >
            <i className={`bi ${filter.icon}`} style={{ color: statusFilter === filter.value ? 'inherit' : filter.color }}></i>
            <span className="visually-hidden">{filter.label}</span>
          </ToggleButton>
        ))}
      </ButtonGroup>
      <Button className='btn-lg ms-auto' type="button" variant="outline-primary" onClick={loadOrders} disabled={loading}>
        <i className="bi bi-arrow-clockwise me-2"></i>Obnovit
      </Button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <Row className="g-2">
          {filteredOrders.length === 0 && (
            <Col xs={12}>
              <Alert variant="secondary" className="mb-0">
                Pro dnešní den nejsou v tomto filtru žádné objednávky.
              </Alert>
            </Col>
          )}
          {filteredOrders.map((o) => {
            const meta = statusMeta(o.stav)
            return (
            <Col xs={6} key={o.idobj}>
              <Card
                className="mechanic-order-card h-100"
                role="button"
                style={{ borderLeft: `3px solid ${meta.border}` }}
                onClick={() => navigate(`/mechanic/order/${o.idobj}`)}
              >
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="small text-muted lh-1">SPZ</div>
                      <div className="fs-3 fw-bold lh-1">{o.spz || '-'}</div>
                    </div>
                    <div className="d-flex align-items-center justify-content-end gap-1">
                      <Badge
                        className="mechanic-order-status-badge small px-2 py-1"
                        style={{ '--mechanic-status-color': meta.color, '--mechanic-status-bg': meta.background }}
                      >
                        {meta.label}
                      </Badge>
                      <Badge bg="secondary" pill className="small px-2 py-1">{o.stani || '-'}</Badge>
                    </div>
                  </div>
                  <div className="mt-3 d-flex justify-content-between align-items-end gap-2">
                    <div className="d-flex align-items-center gap-2 min-w-0">
                      <div>
                        <div className="small text-muted lh-1">Zákazník</div>
                        <div className="fw-semibold">{o.zakaznik || '-'}</div>
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      <div className="small text-muted lh-1">Zakázka</div>
                      <div className="fw-semibold">{o.cislo || '-'}</div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            )
          })}
        </Row>
      )}
    </div>
  )
}

export default DashboardMechanic
